import EPlataforma from '../enums/EPlataforma';

/**
 * PWAUtils - Uma classe de utilidades para PWAs.
 * Fornece métodos para detectar a plataforma de acesso do usuário.
 */
export default class PWAUtils {
  /**
   * Determina a plataforma de onde o usuário está acessando a aplicação.
   * Diferencia entre PWA instalado e acesso via navegador em
   * iOS, Android e Desktop.
   * @returns {string} A plataforma de acesso.
   * Ex: 'Android (Instalado)', 'Navegador (iOS)', etc.
   */
  static obterPlataforma() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    // Verifica se o PWA está rodando em modo standalone (instalado)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;

    // Lógica para PWA Instalado
    if (isStandalone) {
      if (/android/i.test(userAgent)) {
        return EPlataforma.Android;
      }
      // A verificação `!window.MSStream` é uma forma de evitar falsos positivos no Internet Explorer
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
        return EPlataforma.iOS;
      }
      return EPlataforma.Desktop;
    }

    // Lógica para acesso via Navegador
    if (/android/i.test(userAgent)) {
      return EPlataforma.NavegadorAndroid;
    }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      return EPlataforma.NavegadoriOS;
    }
    return EPlataforma.NavegadorDesktop;
  }

  // --- ESTADO INTERNO PRIVADO ---
  private static deferredPrompt: any = null;
  private static isInstallAvailable: boolean = false;

  // --- LÓGICA DE ASSINATURA (SUBSCRIBER PATTERN) ---
  private static availabilitySubscribers: ((isAvailable: boolean) => void)[] = [];

  /**
   * Permite que o código externo (componentes) assine para receber atualizações
   * sobre a disponibilidade de instalação do PWA.
   *
   * @param callback A função a ser chamada quando o status mudar.
   * @returns Uma função para cancelar a assinatura (unsubscribe).
   */
  public static onInstallAvailableChange(callback: (isAvailable: boolean) => void): () => void {
    // Adiciona o novo "assinante" à lista
    PWAUtils.availabilitySubscribers.push(callback);

    // Imediatamente informa ao novo assinante o estado atual
    callback(PWAUtils.isInstallAvailable);

    // Retorna uma função para que o "assinante" possa se remover da lista (evitar memory leaks)
    return () => {
      const index = PWAUtils.availabilitySubscribers.indexOf(callback);
      if (index > -1) {
        PWAUtils.availabilitySubscribers.splice(index, 1);
      }
    };
  }

  /**
   * Notifica todos os "assinantes" sobre uma mudança no estado de disponibilidade.
   * @private
   */
  private static notifySubscribers(): void {
    for (const callback of PWAUtils.availabilitySubscribers) {
      callback(PWAUtils.isInstallAvailable);
    }
  }

  public static initInstaller(): void {
    window.addEventListener('beforeinstallprompt', PWAUtils.handleBeforeInstallPrompt);
  }

  public static async instalar(): Promise<boolean> {
    if (!PWAUtils.deferredPrompt) return false;

    PWAUtils.deferredPrompt.prompt();
    const { outcome } = await PWAUtils.deferredPrompt.userChoice;

    PWAUtils.deferredPrompt = null;
    // Atualiza o estado interno e notifica os assinantes
    PWAUtils.isInstallAvailable = false;
    PWAUtils.notifySubscribers();

    return outcome === 'accepted';
  }

  // --- HANDLERS PRIVADOS ---
  private static handleBeforeInstallPrompt = (e: Event): void => {
    e.preventDefault();
    PWAUtils.deferredPrompt = e;
    // Atualiza o estado interno e notifica os assinantes
    PWAUtils.isInstallAvailable = true;
    PWAUtils.notifySubscribers();
  };
}
